<!--
// app/components/pages/players-player/PlayerSeasonsFilter.vue
-->
<script setup lang="ts">
import type { SeasonPlayerInfo } from "~/src/player/domain/Player";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useSeasonCascade } from "~/app/composables/season/useSeasonCascade";
import { useWebContext } from "~/app/composables/webContext";
import RegionUiService from "~/src/region/infrastructure/ui-services/regionUiService";

type Props = {
  combinedWithMoreFilters?: boolean;
  playerId: number;
  playerSeasons: SeasonPlayerInfo[] | undefined;
  initialFilterValues?: { seasonId: number; seasonClusterId: number; regionId: number };
};

const props = withDefaults(defineProps<Props>(), {
  combinedWithMoreFilters: false,
});

const emit = defineEmits(["change"]);

const { changeSeason } = useSeasonFilter();
const { findLastSeasonClusterForRegion, findLastSeasonInCluster } = useSeasonCascade();
const { findRegionInfoByRegionId, findRegionInfoByCompetitionId, getRegions } = RegionUiService();
const { webContext } = useWebContext();

const playerSeasons = props.playerSeasons || [];

const regionId = defineModel<number>("regionId");
const seasonClusterId = defineModel<number>("seasonClusterId");
const seasonId = defineModel<number>("seasonId");

const seasonOptions = computed((): SeasonPlayerInfo[] => {
  const regionCompetitions = findRegionInfoByRegionId(regionId.value, "competitionId");

  let filtered = playerSeasons.filter(
    s =>
      regionCompetitions?.includes(s.season.competitionId) &&
      s.season.seasonClusterId === seasonClusterId.value &&
      s.season.competition.visible
  );

  return filtered.sort((a, b) => a.season.start - b.season.start);
});

const regions = await getRegions(webContext);
const regionOptions = computed(() => {
  const competitionIds = [...new Set<number>()];

  playerSeasons.forEach(s => {
    const competitionId = s.season?.competitionId;
    if (competitionId !== undefined) {
      competitionIds.push(competitionId);
    }
  });

  const competitionsPlayed = regions.filter(region => {
    return !!region.competitionId.length && region.competitionId?.some(id => competitionIds.includes(id));
  });

  return competitionsPlayed
    .map(region => ({
      id: findRegionInfoByRegionId(region.id, "id"),
      name: findRegionInfoByRegionId(region.id, "name"),
    }))
    .sort((a, b) => a.id - b.id);
});

const { data: seasonClustersAvailable } = await useFetch("/api/v1/competition/season-clusters/available");

const seasonClusterOptions = computed((): { name: string; id: number }[] => {
  const regionCompetitions = findRegionInfoByRegionId(regionId.value, "competitionId");
  const validSeasons = playerSeasons.filter(s => {
    return (
      regionCompetitions &&
      regionCompetitions.includes(s.season.competitionId) &&
      s.season.competition.visible
    );
  });

  const seasonClusterIds = [...new Set(validSeasons.map(s => s.season.seasonClusterId))];

  const matchingClusters = seasonClustersAvailable.value.filter(
    cluster => seasonClusterIds.includes(cluster.id) && cluster.regionIds.includes(regionId.value)
  );

  const simplifiedOptions = matchingClusters.map(cluster => ({
    name: cluster.name,
    id: cluster.id,
  }));

  return simplifiedOptions.length ? simplifiedOptions : undefined;
});

/**
 * Helper function to filter player seasons by region and cluster
 */
const filterSeasonsByRegionAndCluster = (
  seasons: SeasonPlayerInfo[],
  seasonClusterId: number,
  regionId: number
): SeasonPlayerInfo[] => {
  const regionCompetitions = findRegionInfoByRegionId(regionId, "competitionId");
  return seasons.filter(
    s =>
      s.season.seasonClusterId === seasonClusterId &&
      regionCompetitions &&
      regionCompetitions.includes(s.season.competitionId) &&
      s.season.competition.visible
  );
};

/**
 * Computed: Last available season cluster ID for current region
 */
const lastSeasonClusterIdForRegion = computed((): number | undefined => {
  if (!seasonClustersAvailable.value || !regionId.value) {
    return undefined;
  }

  return findLastSeasonClusterForRegion(
    seasonClustersAvailable.value,
    regionId.value,
    playerSeasons,
    filterSeasonsByRegionAndCluster
  );
});

/**
 * Handles user clicking a season button
 */
const handleSeasonClick = (seasonClusterId: number, seasonIdArg: number) => {
  changeSeason(seasonClusterId, seasonIdArg, regionId.value, props.initialFilterValues);
  seasonId.value = seasonIdArg;
  emit("change", seasonId.value);
};

/**
 * Watch regionId changes - automatically select last cluster and season
 * Uses guard to prevent running on initial mount (props handle that)
 */
let isInitialized = false;

watch(
  regionId,
  (newRegion, oldRegion) => {
    // Skip if not initialized yet or no actual change
    if (!isInitialized || newRegion === oldRegion || !newRegion) return;

    const newSeasonClusterId = lastSeasonClusterIdForRegion.value;
    if (!newSeasonClusterId) return;

    seasonClusterId.value = newSeasonClusterId;

    // Cascade to last season in the new cluster
    nextTick(() => {
      const lastSeason = findLastSeasonInCluster(seasonOptions.value);
      if (lastSeason) {
        handleSeasonClick(newSeasonClusterId, lastSeason.season.id);
      }
    });
  }
);

/**
 * Watch seasonClusterId changes - automatically select last season
 * Uses guard to prevent unnecessary triggers
 */
watch(
  seasonClusterId,
  (newCluster, oldCluster) => {
    // Skip if not initialized yet or no actual change
    if (!isInitialized || newCluster === oldCluster || !newCluster) return;

    nextTick(() => {
      const lastSeason = findLastSeasonInCluster(seasonOptions.value);
      if (lastSeason) {
        handleSeasonClick(newCluster, lastSeason.season.id);
      }
    });
  }
);

/**
 * Initialize on mount: set initial cluster and season if not already set
 * This ensures props initialFilterValues are used first, then cascade logic
 */
onMounted(() => {
  // Set initial values if not provided via props
  if (!props.initialFilterValues) {
    const initialClusterId = lastSeasonClusterIdForRegion.value;
    if (initialClusterId && !seasonClusterId.value) {
      seasonClusterId.value = initialClusterId;

      const initialSeason = findLastSeasonInCluster(seasonOptions.value);
      if (initialSeason && !seasonId.value) {
        seasonId.value = initialSeason.season.id;
      }
    }
  }

  // Mark as initialized after setup to enable watchers
  nextTick(() => {
    isInitialized = true;
  });
});
</script>

<template>
  <div class="flex flex-col gap-2 md:flex-row md:flex-end justify-end my-6">
    <div
      v-if="seasonOptions.length"
      :class="['flex rounded-md shadow-sm justify-end ', combinedWithMoreFilters ? 'max-w-max' : '']"
    >
      <!--    Seasons -->
      <button
        v-for="(season, index) in seasonOptions"
        :key="index"
        @click="handleSeasonClick(season.season.seasonClusterId, season.season.id)"
        :class="[
          'kql-season-button',
          {
            '!bg-primary !text-black': season.season.id === seasonId,
          },
        ]"
      >
        {{ season.season.displayName }}
      </button>

      <!-- Clusters -->
      <select
        id="seasonCluster"
        class="kql-select text-[10px] bigsm:text-xs lg:text-sm border-l rounded-e-lg"
        v-model="seasonClusterId"
      >
        <option
          v-for="seasonCluster in seasonClusterOptions"
          :key="seasonCluster.id"
          :value="seasonCluster.id"
        >
          {{ seasonCluster.name }}
        </option>
      </select>
    </div>

    <div v-else class="flex px-4 lg:px-0 shadow-sm justify-end">
      <div class="h-10 w-full max-w-xl bg-gray-800 rounded-lg animate-pulse"></div>
    </div>

    <!--    Regions-->
    <select
      v-if="!!regionId && regionOptions.length > 0"
      id="regionOptions"
      class="kql-select order-first md:order-last md:ml-6 text-[10px] bigsm:text-xs lg:text-sm border-l rounded-lg"
      v-model="regionId"
    >
      <option v-for="region in regionOptions" :key="region.id" :value="region.id">
        {{ region.name }}
      </option>
    </select>
  </div>
</template>

<style scoped></style>
